import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types'
import { Button, Rate, Typography } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

const SET_SIZE = 3;
const tooltips = {
  CUTE: {
    0: 'Extremely disgusting',
    1: 'Very disgusting',
    2: 'Disgusting',
    3: 'Slightly disgusting',
    4: 'Neither disgusting nor cute',
    5: 'Slightly cute',
    6: 'Cute',
    7: 'Very cute',
    8: 'Extremely cute'
  },
  BEAUTIFUL: {
    0: 'Extremely ugly',
    1: 'Very ugly',
    2: 'Ugly',
    3: 'Slightly ugly',
    4: 'Neither ugly nor beautiful',
    5: 'Slightly beautiful',
    6: 'Beautiful',
    7: 'Very beautiful',
    8: 'Extremely beautiful'
  },
  DANGEROUS: {
    0: 'Not dangerous',
    1: 'Slightly dangerous',
    2: 'Slightly dangerous +',
    3: 'Slightly dangerous ++',
    4: 'Dangerous',
    5: 'Very dangerous',
    6: 'Very dangerous +',
    7: 'Very dangerous ++',
    8: 'Extremely dangerous'
  },
  ENDANGERED: {
    0: 'Not threatened',
    1: 'Near threatened',
    2: 'Vulnerable',
    3: 'Vulnerable',
    4: 'Endangered',
    5: 'Endangered',
    6: 'Critically endangered',
    7: 'Critically endangered',
    8: 'Extinct in the wild'
  },
  INTELLIGENT: {
    0: 'Not intelligent',
    1: 'Slightly intelligent',
    2: 'Slightly intelligent +',
    3: 'Slightly intelligent ++',
    4: 'Intelligent',
    5: 'Very intelligent',
    6: 'Very intelligent +',
    7: 'Very intelligent ++',
    8: 'Extremely intelligent'
  },
  IMPORTANT_FOR_ECOSYSTEMS: {
    0: 'Extremely harmful',
    1: 'Very harmful',
    2: 'Harmful',
    3: 'Slightly harmful',
    4: 'Neither harmful nor important',
    5: 'Slightly important',
    6: 'Important',
    7: 'Very important',
    8: 'Extremely important'
  },
}

Object.entries(tooltips).forEach(([ type, perType ]) => {
  tooltips[type] = new Array(9).fill(undefined).map((_, i) => {
    return tooltips[type][i]
  })
})

const getClosest = (index = 0, arr = []) => {
  if(arr[index]) {
    return arr[index];
  }
  let bottom = 0, top = -1;
  for (let i = 0; i < arr.length; i++) {
    if(!arr[i]) {
      continue;
    }
    if(i < index) {
      bottom = i;
    } else if( i > index) {
      top = i;
    }
    if(top > 0) {
      break;
    }
  }
  const diffBottom = Math.abs(index - bottom);
  const diffTop = Math.abs(index - top);

  return diffBottom < diffTop ? arr[bottom] : arr[top]
}

const Ratings = ({ values, onChange, types, viewport, onSubmit, submitDisabled }) => {
  const [ firstSetAnswered, setFirstSetAnswered ] = useState(false);
  const [ showingSecondSet, setShowingSecondSet ] = useState(false);
  const [ descriptionPerType, setDescriptionPerType ] = useState({});

  const onRatingChange = useCallback((type, val) => {
    setDescriptionPerType(prev => {
      let currentIndex = val - 1;
      let desc = getClosest(currentIndex, tooltips[type.id]);
      while (!desc) {
        desc = tooltips[type.id][--currentIndex];
      }
      return {
        ...prev,
        [type.id]: desc
      }
    })
    onChange({
      ...values,
      [type.id]: val
    })
  }, [ values, onChange ])

  useEffect(() => {
    setFirstSetAnswered(types.slice(0, SET_SIZE).every(t => !!values[t.id]))
  }, [ values, types ])

  useEffect(() => {
    setShowingSecondSet(firstSetAnswered)
  }, [ firstSetAnswered ])


  return (
    <div className="ratings-wrapper">
      <Typography.Title level={3} className="quiz-title">
        How would you rate this animal in terms of it being
      </Typography.Title>
      {viewport === 'mobile' && firstSetAnswered && !showingSecondSet && (
        <Button onClick={() => setShowingSecondSet(true)} className="frw-btn" shape="circle" icon={<ArrowRightOutlined />} />
      )}
      {viewport === 'mobile' && showingSecondSet && (
        <Button onClick={() => setShowingSecondSet(false)} className="back-btn" shape="circle" icon={<ArrowLeftOutlined />} />
      )}
      {(!showingSecondSet || viewport === 'desktop') && (
        <div className={`set first-set ${!showingSecondSet && 'showing'}`}>
          {types.slice(0, SET_SIZE).map((type) => (
            <React.Fragment key={type.id}>
              <Typography.Title className={type.id.toLowerCase()} level={3}>{type.text}</Typography.Title>
              <div className="rating-desc-container">
                <Rate className={`ratings ${type.id.toLowerCase()}`} value={values[type.id] || 0} allowClear={false}
                      count={9}
                      onChange={v => onRatingChange(type, v)} />
                <Typography.Text className="description">{!!values[type.id] && descriptionPerType[type.id]}&nbsp;</Typography.Text>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
      {(showingSecondSet || viewport === 'desktop') && (
        <>
          <div className={`set second-set ${showingSecondSet && viewport === 'mobile' && 'right-align'}`}>
            {types.slice(SET_SIZE).map((type) => (
              <React.Fragment key={type.id + 12}>
                <Typography.Title className={type.id.toLowerCase()} level={3}>{type.text}</Typography.Title>
                <div className="rating-desc-container">
                  <Rate className={`ratings ${type.id.toLowerCase()}`} value={values[type.id] || 0} allowClear={false}
                        count={9}
                        onChange={v => onRatingChange(type, v)} />
                  <Typography.Text className="description">{!!values[type.id] && descriptionPerType[type.id]}&nbsp;</Typography.Text>
                </div>

              </React.Fragment>
            ))}
          </div>
          <div className="rate-btn-container">
            <Button className="rate-btn" type="primary" size="large" disabled={submitDisabled} onClick={onSubmit}>Rate</Button>
          </div>
        </>
      )}
    </div>
  )

  // return (
  //   <Row align="middle">
  //     <Col span={24} className={`set first-set ${!showingSecondSet && 'showing'}`}>
  //       {firstSetAnswered && <Button onClick={toggleSet} shape="circle" icon={<ArrowRightOutlined />} />}
  //       {types.slice(0, SET_SIZE).map((type) => (
  //         <Row key={type.id} gutter={24} wrap={false}>
  //           <Col xs={24} lg={8}><Typography.Title level={3}>{type.text}</Typography.Title></Col>
  //           <Col xs={24} lg={16}>
  //             <Rate className="ratings" tooltips={tooltips} value={values[type.id] || 0} allowClear={false} count={10}
  //                   onChange={v => onRatingChange(type, v)} />
  //           </Col>
  //         </Row>
  //       ))}
  //     </Col>
  //     <Col span={24} className={`set second-set ${showingSecondSet && 'showing'}`}>
  //       {firstSetAnswered && <Button onClick={toggleSet} shape="circle" icon={<ArrowLeftOutlined />} />}
  //       {types.slice(SET_SIZE).map((type) => (
  //         <Row key={type.id} gutter={24} wrap={false}>
  //           <Col xs={24} lg={8}><Typography.Title level={3}>{type.text}</Typography.Title></Col>
  //           <Col xs={24} lg={16}>
  //             <Rate className="ratings" tooltips={tooltips} value={values[type.id] || 0} allowClear={false} count={10}
  //                   onChange={v => onRatingChange(type, v)} />
  //           </Col>
  //         </Row>
  //       ))}
  //     </Col>
  //   </Row>
  // )
}

Ratings.propTypes = {
  types: PropTypes.arrayOf(PropTypes.string),
  values: PropTypes.objectOf(PropTypes.number),
  onChange: PropTypes.func
}

export default Ratings;